import { 
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";

// Import prebuilt card components
import CareFlowCard from "./cards/CareFlowCard";
import EncounterManagementCard from "./cards/EncounterManagementCard";
import AIIntakeCard from "./cards/AIIntakeCard";
import AmbientScribeCard from "./cards/AmbientScribeCard";
import ChartPrepCard from "./cards/ChartPrepCard";
import ClinicalDocumentationCard from "./cards/ClinicalDocumentationCard";
import Patient360Card from "./cards/Patient360Card";
import CommunicationHubCard from "./cards/CommunicationHubCard";
import OccupationalHealthCard from "./cards/OccupationalHealthCard";

// Brand colors
const PRIMARY_BLUE = "#1356a6";

// All platform cards in a flat array
const allPlatformCards: React.ComponentType[] = [
  // Workflow & Operations
  CareFlowCard,
  EncounterManagementCard,
  // Clinical Intelligence
  AmbientScribeCard,
  ChartPrepCard,
  AIIntakeCard,
  // Occupational Health & Employer Services
  OccupationalHealthCard,
  ClinicalDocumentationCard,
  // Patient Management & Engagement
  Patient360Card,
  CommunicationHubCard
];

// Auto-cycle interval for cards (in ms)
const AUTO_CYCLE_INTERVAL = 4000;

export default function PlatformCapabilities() {
  // Current card index
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  // Whether auto-cycling is paused (on hover)
  const [isPaused, setIsPaused] = useState(false);

  const totalCards = allPlatformCards.length;

  // Navigate to next/previous card
  const goToNextCard = useCallback(() => {
    setActiveCardIndex((prev) => (prev + 1) % totalCards);
  }, [totalCards]);

  const goToPrevCard = useCallback(() => {
    setActiveCardIndex((prev) => (prev - 1 + totalCards) % totalCards);
  }, [totalCards]);

  // Auto-cycle cards
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % totalCards);
    }, AUTO_CYCLE_INTERVAL);

    return () => clearInterval(interval);
  }, [isPaused, totalCards]);

  // Render the current card
  const renderCurrentCard = () => {
    const CardComponent = allPlatformCards[activeCardIndex];
    
    if (CardComponent) {
      return <CardComponent />;
    }
    return null;
  };

  return (
    <section id="platform-features" className="py-32">
      <div className="w-full px-4">

        {/* Platform Carousel */}
        <div className="mb-16">
          {/* Main Carousel Container */}
          <div 
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Card Navigation Arrows */}
            <button
              onClick={goToPrevCard}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
              style={{ left: '-24px' }}
              aria-label="Previous card"
            >
              <ChevronLeft className="w-6 h-6" style={{ color: PRIMARY_BLUE }} />
            </button>
            <button
              onClick={goToNextCard}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
              style={{ right: '-24px' }}
              aria-label="Next card"
            >
              <ChevronRight className="w-6 h-6" style={{ color: PRIMARY_BLUE }} />
            </button>

            {/* Card Container */}
            <div className="rounded-3xl border-2 overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm" style={{ borderColor: `${PRIMARY_BLUE}20` }}>
              <div className="p-4 md:p-6">
                {/* Section Header - Inside Carousel */}
                <div className="text-center mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>
                    Platform Features
                  </h2>
                  <p className="text-xl sm:text-2xl max-w-2xl mx-auto" style={{ color: PRIMARY_BLUE }}>
                    Comprehensive AI-powered solutions across every aspect of urgent care
                  </p>
                </div>

                {/* Card Content with Animation */}
                <div className="relative min-h-[700px]">
                  {allPlatformCards.map((_, cardIndex) => (
                    <div
                      key={cardIndex}
                      className={`transition-all duration-500 absolute inset-0 ${
                        activeCardIndex === cardIndex
                          ? "opacity-100 translate-x-0 z-10"
                          : activeCardIndex > cardIndex
                            ? "opacity-0 -translate-x-full z-0"
                            : "opacity-0 translate-x-full z-0"
                      }`}
                    >
                      {activeCardIndex === cardIndex && renderCurrentCard()}
                    </div>
                  ))}
                </div>

                {/* Card Dots Indicator */}
                <div className="flex justify-center gap-2 mt-6">
                  {allPlatformCards.map((_, cardIndex) => (
                    <button
                      key={cardIndex}
                      onClick={() => setActiveCardIndex(cardIndex)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        activeCardIndex === cardIndex ? "w-8" : "w-2"
                      }`}
                      style={{
                        backgroundColor: activeCardIndex === cardIndex ? PRIMARY_BLUE : `${PRIMARY_BLUE}30`
                      }}
                      aria-label={`Go to card ${cardIndex + 1}`}
                    />
                  ))}
                </div>

                {/* Auto-cycle indicator */}
                <div className="flex justify-center items-center gap-2 mt-4">
                  <div 
                    className={`w-2 h-2 rounded-full transition-colors ${isPaused ? 'bg-gray-300' : 'animate-pulse'}`}
                    style={{ backgroundColor: isPaused ? undefined : PRIMARY_BLUE }}
                  />
                  <span className="text-xs" style={{ color: `${PRIMARY_BLUE}80` }}>
                    {isPaused ? 'Paused' : 'Auto-cycling'} • Card {activeCardIndex + 1} of {totalCards}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

